import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/bot-permission-request`;
const options = { withCredentials: true, timeout: 15000 };

export const useBotPermission = () => {
  const [data, setData] = useState(null), [review, setReview] = useState(null);
  const [loading, setLoading] = useState(true), [busy, setBusy] = useState(false);
  const [error, setError] = useState(''), [notice, setNotice] = useState('');
  const generation = useRef(0), mounted = useRef(true), writing = useRef(false);

  const load = useCallback(async (forReview = false) => {
    if (writing.current) return;
    const turn = ++generation.current;
    setLoading(true); setError('');
    try {
      const result = await axios.get(API, options);
      if (!mounted.current || turn !== generation.current) return;
      setData(result.data);
      if (forReview) setReview(result.data);
    } catch {
      if (mounted.current && turn === generation.current) setError('loadError');
    } finally {
      if (mounted.current && turn === generation.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    mounted.current = true; load();
    return () => { mounted.current = false; ++generation.current; };
  }, [load]);
  useEffect(() => {
    const refresh = () => { if (!review && !writing.current && document.visibilityState === 'visible') load(); };
    window.addEventListener('focus', refresh);
    return () => window.removeEventListener('focus', refresh);
  }, [load, review]);

  const save = async (withdraw = false) => {
    if (writing.current || (!withdraw && !review)) return;
    writing.current = true; ++generation.current;
    setBusy(true); setLoading(false); setError(''); setNotice('');
    try {
      const result = await axios.post(withdraw ? `${API}/withdraw` : API, withdraw ? {} : {
        expected_version: review.version, scope_token: review.scope_token,
        policy_version: review.policy_version, acknowledged: true,
      }, options);
      if (!mounted.current) return;
      setData(result.data); setReview(null);
      setNotice(withdraw ? 'withdrawnNotice' : 'success');
    } catch (failure) {
      if (!mounted.current) return;
      setReview(null);
      setError(failure.response?.status === 409 ? 'conflict' : 'saveError');
    } finally {
      writing.current = false;
      if (mounted.current) setBusy(false);
    }
  };
  return { data, review, loading, busy, error, notice, load, save,
    close: () => { if (!writing.current) setReview(null); },
    open: () => { setNotice(''); load(true); } };
};